<script setup lang="ts">
import { helpers } from '@vuelidate/validators'
import { useVModel } from '@vueuse/core'
import cardValidator from 'card-validator'
import { DateTime } from 'luxon'

import { ArgentoInsurancePaymentPeriodicities } from '~/config'
import type { Coverage, PaymentInfo } from '~/layers/forms/models'
import { Form } from '~/layers/forms/models'
import { requiredIfValidator, requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: PaymentInfo
  coverage: Coverage
}

const props = defineProps<Props>()

const promotionalCode = ref<string | null>(null)

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)
const paymentConditionId = ref<number>()
const toast = useToast()

const { LocalConfigurationStore, RemoteConfigurationStore } = stores()

const { constraints } = useContractConstraints()

const paymentMethods = computed(() => {
  const {
    argentoInsuranceAplicableCalculatedPaymentConditions,
    argentoInsuranceAplicablePaymentConditions,
  } = props.coverage

  const { planCode } = props.coverage
  let aplicablePaymentConditions: string[] | null = null

  if (planCode) {
    aplicablePaymentConditions =
      LocalConfigurationStore.value?.planParameters?.config.find((bpc) =>
        bpc.coverageTypeOrPlanCodes.includes(planCode)
      )?.aplicablePaymentConditions ?? null
  }

  let paymentMethods =
    argentoInsuranceAplicableCalculatedPaymentConditions ??
    argentoInsuranceAplicablePaymentConditions?.map((i) => i.condicionPago)

  if (aplicablePaymentConditions) {
    paymentMethods = paymentMethods?.filter((pm) =>
      aplicablePaymentConditions.includes(pm?.codigo ?? '_')
    )
  }

  return paymentMethods?.map((i) => ({
    value: i?.condicionPagoId,
    label: i?.nombre ?? i?.descripcion,
  }))
})

const requirePaymentMethodSelection = computed(() =>
  Boolean(paymentMethods.value && paymentMethods.value?.length > 1)
)

const cardValidation = computed(() => ({
  number: cardValidator.number(model.value.cc),
  exp: model.value.exp
    ? cardValidator.expirationDate(DateTime.fromJSDate(model.value.exp).toFormat('MM/yy'), 10)
    : null,
}))

const acceptPromotionalCode = computed(() => {
  if (
    RemoteConfigurationStore.value?.initBy === 'ByProducto' &&
    RemoteConfigurationStore.value.form?.aplicaDescuento
  )
    return true
  if (
    RemoteConfigurationStore.value?.initBy === 'BySubRamo' &&
    props.coverage.avaliablePromotionalCode
  )
    return true
  return false
})

const v$ = useValidation(
  () => ({
    cc: {
      required: requiredValidator,
      valid: helpers.withMessage(
        'El número de tarjeta no es valido.',
        () => cardValidation.value.number.isValid
      ),
    },
    exp: {
      required: requiredValidator,
      valid: helpers.withMessage(
        'La fecha de expiración no es valida.',
        () => cardValidation.value.exp?.isValid ?? false
      ),
    },
    ccv: {
      required: requiredIfValidator(computed(() => !constraints.value?.ignoreCreditCardCCV)),
    },
    argentoInsurancePaymentConditionId: {
      required: requiredIfValidator(requirePaymentMethodSelection),
    },
  }),
  model,
  {
    $scope: Form.paymentInfo,
    $registerAs: Form.paymentInfo,
  }
)

const {
  execute: getDiscount,
  data: discount,
  status: discountStatus,
} = useSiteApi('/api/BrokerProductoDescuento', {
  method: 'POST',
  query: computed(() => ({
    BrokerProductoId:
      props.coverage.argentoInsuranceCoverageRequestData?.brokerProductoId ?? undefined,
    Tag: promotionalCode.value ?? undefined,
  })),
  transform(input?: SiteApiSchema<'BrokerProductoDescuentoModel'>) {
    if (input) return input
  },
  watch: false,
  immediate: false,
})

const validatePromotionalCode = async () => {
  if (!promotionalCode.value) return
  const alertForInvalid = (message?: string) =>
    toast.add({
      severity: 'warn',
      summary: 'Código promocional',
      detail: message ?? 'El codigo promocional proporcionado no es aplicable.',
      life: 5000,
    })

  await getDiscount()

  if (!discount.value) {
    alertForInvalid()
    return
  }

  const {
    tag: discountTag,
    tipo: discountType,
    valor: discountAmount,
    inicioVigencia: startDate,
    finVigencia: expirationDate,
    estado: discountStatus,
  } = discount.value

  const validations = {
    promotionalCodeExists: discountTag?.toUpperCase() === promotionalCode.value?.toUpperCase(),
    isActive: discountStatus === 'ACTIVO',
    inForce: !startDate || (!!startDate && Date.parse(startDate) < Date.now()),
    hasNotExpired: !expirationDate || (!!expirationDate && Date.parse(expirationDate) > Date.now()),
  }

  const promotionalCodeIsValid = [
    validations.promotionalCodeExists,
    validations.isActive,
    validations.inForce,
    validations.hasNotExpired,
  ].every(Boolean)

  const getErrorMessage = () => {
    if (!validations.promotionalCodeExists) return 'El código promocional no existe'
    if (!validations.isActive || !validations.inForce) return 'El código promocional no está activo'
    if (!validations.promotionalCodeExists)
      return `El código promocional ${expirationDate ? `expiró el ${DateTime.fromISO(expirationDate).toFormat('dd/mm/yy')}` : 'ya expiró'}}`
  }

  if (
    !promotionalCodeIsValid ||
    !discountTag ||
    !discountType ||
    !discountAmount ||
    discountAmount <= 0
  ) {
    alertForInvalid(getErrorMessage())
    return
  } else {
    toast.add({
      severity: 'success',
      summary: 'Código promocional',
      detail: 'Se ha aplicado el descuento al monto a pagar.',
      life: 5000,
    })
  }

  switch (discountType) {
    case 'Monto':
      model.value.discountType = 'Monto'
      break
    case 'Porcentaje':
      model.value.discountType = 'Porcentaje'
      break
  }

  model.value.discountTag = discountTag
  model.value.discountAmount = discountAmount
}

watch(
  () => model.value.cc,
  () => {
    model.value.type = cardValidation.value.number.card?.niceType.toUpperCase() ?? null
  }
)

watch(promotionalCode, () => {
  const { discountTag, discountType, discountAmount } = model.value

  model.value.discountTag = null
  model.value.discountAmount = null
  model.value.discountType = null

  if (Boolean(discountTag) || Boolean(discountType) || Boolean(discountAmount)) {
    toast.add({
      severity: 'warn',
      summary: 'Código promocional',
      detail: 'El descuento aplicado fue anulado debido a que se modifico el código promocinal.',
      life: 5000,
    })
  }
})

watch(
  () => props.coverage.argentoInsuranceCoverageRequestData?.brokerProductoId,
  (n, o) => {
    if (n === o) return
    model.value.discountTag = null
    model.value.discountAmount = null
    model.value.discountType = null
    promotionalCode.value = null
  }
)

watch(
  paymentConditionId,
  (n) => {
    if (!n) return

    const {
      argentoInsuranceAplicablePaymentConditions,
      argentoInsuranceAplicableCalculatedPaymentConditions,
    } = props.coverage

    const selectedPaymentCondition = (
      argentoInsuranceAplicablePaymentConditions ??
      argentoInsuranceAplicableCalculatedPaymentConditions
    )?.find((pm) => pm.condicionPagoId === n)

    if (!selectedPaymentCondition) return
    model.value.argentoInsurancePaymentConditionId =
      selectedPaymentCondition?.condicionPagoId ?? null
    model.value.argentoInsurancePaymentPeriodicity =
      Object.values(ArgentoInsurancePaymentPeriodicities).find(
        (pp) => pp.id.toString() === selectedPaymentCondition.frecuenciaPago
      )?.value ?? null
  },
  {
    deep: true,
  }
)

watch(
  () => props.coverage,
  () => {
    paymentConditionId.value = undefined
    model.value.argentoInsurancePaymentConditionId = null
    if (paymentMethods.value?.length === 1)
      paymentConditionId.value = paymentMethods.value[0]?.value
  },
  {
    deep: true,
  }
)
</script>

<template>
  <FormsGlobalContainer
    v-slot="{ sectionClasses }"
    :identifier="Form.paymentInfo"
    :cols="1"
  >
    <!-- <FormsGlobalTitle
      text="Forma de pago"
      icon-class="icon-[ph--credit-card-bold]"
    /> -->
    <InputField
      label="Número de tarjeta"
      class="col-span-full"
      :error="v$.cc?.$error"
      :error-message="v$.cc?.$errors[0]?.$message.toString()"
    >
      <InputAddon :sufix="{ text: cardValidation.number.card?.niceType }">
        <PvInputMask
          v-model="model.cc"
          mask="9999-9999-9999-9999"
          placeholder="XXXX-XXXX-XXXX-XXXX"
        />
      </InputAddon>
    </InputField>

    <div :class="sectionClasses({ minCols: 3 })">
      <InputField
        label="Fecha de expiración"
        class="col-span-2"
        :error="v$.exp?.$error"
        :error-message="v$.exp?.$errors[0]?.$message.toString()"
      >
        <PvDatePicker
          v-model="model.exp"
          :min-date="new Date()"
          view="month"
          date-format="mm/y"
          placeholder="MM/AA"
        />
      </InputField>

      <InputField
        v-if="!constraints?.ignoreCreditCardCCV"
        label="CVV"
        :error="v$.ccv?.$error"
        :error-message="v$.ccv?.$errors[0]?.$message.toString()"
      >
        <PvInputMask
          v-model="model.ccv"
          mask="999"
          placeholder="XXX"
        />
      </InputField>
    </div>

    <InputField
      v-if="requirePaymentMethodSelection"
      label="Forma de pago"
      :error="v$.argentoInsurancePaymentConditionId?.$error"
      :error-message="v$.argentoInsurancePaymentConditionId?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="paymentConditionId"
        :options="paymentMethods"
        option-value="value"
        option-label="label"
      >
        <template #value="slotProps">
          <span class="text-sm">
            {{
              paymentMethods?.find((pm) => pm.value === slotProps.value)?.label ??
              slotProps.placeholder
            }}
          </span>
        </template>
      </PvSelect>
    </InputField>

    <InputField
      v-if="acceptPromotionalCode"
      label="Código promocional"
      class="col-span-full"
      :loading="discountStatus === 'pending'"
    >
      <PvInputGroup>
        <PvInputText
          v-model="promotionalCode"
          :disabled="discountStatus === 'pending'"
          @blur="validatePromotionalCode()"
        />
        <PvButton @click="validatePromotionalCode()">
          <span class="icon-[ph--magnifying-glass-bold]" />
        </PvButton>
      </PvInputGroup>
    </InputField>

    <slot name="resume" />
  </FormsGlobalContainer>
</template>
